"use client";

import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { useTranslations } from "next-intl";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  isNewsletterErrorKey,
  newsletterLeadSchema,
  type NewsletterLeadInput,
} from "@/lib/validators/lead.schema";
import CustomButton from "../ui/custom-button";

// Error-key suffixes the submit handler may receive from the route (schema keys
// plus the route-only rateLimited / unexpected). Kept as a display allow-list so
// an unexpected server string never renders as a raw key path.
const SUBMIT_ERROR_KEYS = [
  "emailInvalid",
  "emailTooLong",
  "rateLimited",
  "unexpected",
] as const;

interface NewsletterCTASectionProps {
  className?: string;
}

export function NewsletterCTASection({ className }: NewsletterCTASectionProps) {
  const t = useTranslations("newsletter");
  const [subscribed, setSubscribed] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const successRef = useRef<HTMLDivElement>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<NewsletterLeadInput>({
    resolver: zodResolver(newsletterLeadSchema),
    defaultValues: { email: "" },
    // Validate on submit, then clear the error as the visitor fixes it — the
    // same interaction model as the contact form.
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  // On success the form unmounts and the status panel takes its place; move
  // focus there so keyboard / screen-reader users aren't dropped to <body>.
  useEffect(() => {
    if (subscribed) successRef.current?.focus();
  }, [subscribed]);

  // Both the schema and the route speak in error-key suffixes. Translate a known
  // one; anything unrecognized falls back to the generic message rather than
  // rendering a raw key path to the visitor.
  const translateError = (key?: string | null) =>
    key && (SUBMIT_ERROR_KEYS as readonly string[]).includes(key)
      ? t(`errors.${key}`)
      : t("errors.unexpected");

  const emailKey = errors.email?.message;
  const emailError = errors.email
    ? emailKey && isNewsletterErrorKey(emailKey)
      ? t(`errors.${emailKey}`)
      : t("errors.unexpected")
    : null;

  const onSubmit = async (values: NewsletterLeadInput) => {
    setSubmitError(null);

    try {
      const response = await fetch("/api/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });

      if (!response.ok) {
        // The route returns a stable error KEY (never developer copy), so
        // translate it, falling back to the generic message for a body we can't
        // read or don't recognize.
        const body = (await response.json().catch(() => null)) as {
          error?: string;
        } | null;
        setSubmitError(translateError(body?.error));
        return;
      }

      setSubscribed(true);
    } catch {
      // fetch rejects on a network failure with an untranslated technical
      // message; never surface that. The typed email is preserved so a failed
      // send does not cost the visitor what they entered.
      setSubmitError(t("errors.unexpected"));
    }
  };

  return (
    <section
      className={cn(
        "px-4 py-24 font-outfit  md:px-20  w-full bg-site",
        className
      )}
    >
      <div className=" p-6 py-10 md:py-16 md:p-16  rounded-3xl  bg-yellow-500  w-full  ">
        <div className=" w-full ">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8  items-center">
            {/* Left side - Content */}
            <div className="">
              <h2 className="text-3xl text-center font-semibold md:text-left text-black mb-4">
                {t('title')}
              </h2>
              <p className="text-black text-center md:text-left text-lg">
                {t('subtitle')}
              </p>
            </div>

            {/* Right side - Form */}
            <div>
              {subscribed ? (
                <div
                  ref={successRef}
                  tabIndex={-1}
                  role="status"
                  className="flex items-start gap-3 rounded-xl border border-black/20 bg-black/5 p-4 text-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black"
                >
                  <CheckCircle2
                    aria-hidden="true"
                    className="mt-0.5 h-6 w-6 shrink-0"
                  />
                  <div>
                    <p className="font-semibold">{t("successTitle")}</p>
                    <p className="text-sm">{t("successSubtitle")}</p>
                  </div>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit(onSubmit)}
                  noValidate
                  className="space-y-4"
                >
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1">
                      <Input
                        type="email"
                        aria-label={t("emailPlaceholder")}
                        placeholder={t("emailPlaceholder")}
                        aria-invalid={emailError ? true : undefined}
                        aria-describedby={
                          emailError ? "newsletter-email-error" : undefined
                        }
                        className="w-full bg-yellow-500 border-[1.5px] border-black text-black placeholder:text-black/70 rounded-xl h-12 px-4 focus:outline-none focus:ring-2 focus:ring-black focus:border-black aria-[invalid=true]:border-red-700"
                        {...register("email")}
                      />
                    </div>
                    <CustomButton
                      variant="capsule"
                      size="lg"
                      disabled={isSubmitting}
                      leadingIcon={
                        isSubmitting ? (
                          <Loader2
                            aria-hidden="true"
                            className="h-4 w-4 animate-spin"
                          />
                        ) : undefined
                      }
                      className="hover:bg-black text-white !px-5 bg-site-card !py-6 font-medium"
                    >
                      {isSubmitting ? t("subscribing") : t("subscribe")}
                    </CustomButton>
                  </div>

                  {emailError && (
                    <p
                      id="newsletter-email-error"
                      role="alert"
                      className="flex items-center gap-2 text-sm font-medium text-red-900"
                    >
                      <AlertTriangle
                        aria-hidden="true"
                        className="h-4 w-4 shrink-0"
                      />
                      {emailError}
                    </p>
                  )}

                  {submitError && (
                    <p
                      role="alert"
                      className="flex items-center gap-2 text-sm font-medium text-red-900"
                    >
                      <AlertTriangle
                        aria-hidden="true"
                        className="h-4 w-4 shrink-0"
                      />
                      {submitError}
                    </p>
                  )}

                  <p className="text-black text-sm text-center md:text-left">
                    {t("privacyText")}{" "}
                    <a href={"/privacy"} className="underline hover:no-underline">
                      {t("privacyLinkLabel")}
                    </a>
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
